import React, { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';

interface TerminalProps {
  /** The command prompt (default: "root@operator:~#" for root, or "operator@host:~$" for user) */
  prompt?: string;
  /** The command to display */
  command: string;
  /** The output from the command (optional) */
  output?: string;
  /** Whether to animate the typing effect (default: false) */
  animate?: boolean;
  /** Whether to show the copy button (default: true) */
  copyable?: boolean;
  /** Whether this is a root prompt (# vs $) */
  isRoot?: boolean;
}

/**
 * Terminal Component
 *
 * Renders a classic hacker-style terminal with black background and green text.
 * Features:
 * - Black background (#0a0a0a), green text (#00ff00 variants)
 * - Monospace font (Fira Code with ligatures)
 * - Prompt + command + output display
 * - Copy functionality (copies command only)
 * - Optional animated typing effect
 * - Syntax highlighting for commands, flags, and output
 */
export const Terminal: React.FC<TerminalProps> = ({
  prompt,
  command,
  output,
  animate = false,
  copyable = true,
  isRoot = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [displayedCommand, setDisplayedCommand] = useState(animate ? '' : command);
  const [displayedOutput, setDisplayedOutput] = useState(animate ? '' : output || '');
  const [showCursor, setShowCursor] = useState(animate);
  const [phase, setPhase] = useState<'command' | 'output'>('command');

  // Default prompt based on root/user
  const defaultPrompt = isRoot ? 'root@operator:~#' : 'operator@host:~$';
  const finalPrompt = prompt || defaultPrompt;

  // Animated typing effect
  useEffect(() => {
    if (!animate) return;

    let commandIndex = 0;
    let outputIndex = 0;
    let currentPhase: 'command' | 'output' = 'command';

    const typingInterval = setInterval(() => {
      if (currentPhase === 'command' && commandIndex < command.length) {
        setDisplayedCommand(command.slice(0, commandIndex + 1));
        commandIndex++;
      } else if (currentPhase === 'command' && commandIndex >= command.length) {
        currentPhase = 'output';
        setPhase('output');
        setShowCursor(false);
        // Small delay before output appears
        setTimeout(() => setShowCursor(true), 100);
      } else if (currentPhase === 'output' && output && outputIndex < output.length) {
        setDisplayedOutput(output.slice(0, outputIndex + 1));
        outputIndex++;
      } else {
        setShowCursor(false);
        clearInterval(typingInterval);
      }
    }, 30); // 30ms per character for smooth typing

    return () => clearInterval(typingInterval);
  }, [animate, command, output]);

  // Copy to clipboard functionality
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Syntax highlighting for command (flags in yellow-green)
  const highlightCommand = (cmd: string) => {
    // Split by flags (starts with - or --)
    const parts = cmd.split(/(\s-{1,2}[a-zA-Z0-9-]+)/g);
    return parts.map((part, index) => {
      if (part.match(/^\s-{1,2}[a-zA-Z0-9-]+/)) {
        return (
          <span key={index} className="text-yellow-400">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Detect error output (lines starting with "Error:", "error:", or containing "Permission denied")
  const isError = output && (
    output.toLowerCase().includes('error') ||
    output.toLowerCase().includes('permission denied') ||
    output.toLowerCase().includes('cannot') ||
    output.toLowerCase().includes('failed')
  );

  return (
    <div className="terminal-container my-6 relative group">
      {/* Terminal Window */}
      <div className="terminal-window bg-[#0a0a0a] rounded-lg border border-gray-800 overflow-hidden shadow-2xl">
        {/* Terminal Header (classic window buttons) */}
        <div className="terminal-header bg-gray-900 px-4 py-2 flex items-center gap-2 border-b border-gray-800">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="ml-2 text-xs text-gray-500 font-mono">
            terminal — operator@datacenter
          </span>
        </div>

        {/* Terminal Content */}
        <div className="terminal-content p-4 font-mono text-sm relative">
          {/* Copy Button */}
          {copyable && (
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-green-400 rounded transition-all opacity-0 group-hover:opacity-100"
              aria-label="Copy command"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Prompt + Command */}
          <div className="flex items-start mb-1">
            <span className="text-green-600 mr-2 select-none">{finalPrompt}</span>
            <span className="text-green-400 flex-1">
              {highlightCommand(displayedCommand)}
              {showCursor && animate && (
                <span className="inline-block w-2 h-4 bg-green-400 ml-1 animate-pulse"></span>
              )}
            </span>
          </div>

          {/* Output */}
          {(displayedOutput || output) && (
            <div className={`whitespace-pre-wrap ${isError ? 'text-red-400' : 'text-green-300'} mt-2 leading-relaxed`}>
              {displayedOutput}
              {showCursor && animate && phase === 'output' && (
                <span className="inline-block w-2 h-4 bg-green-300 ml-1 animate-pulse"></span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-green-500/5 rounded-lg blur-xl -z-10 group-hover:bg-green-500/10 transition-all"></div>
    </div>
  );
};

/**
 * TerminalBlock Component
 *
 * Wrapper for parsing raw terminal text content.
 * Automatically detects prompts, commands, and output.
 *
 * Usage in markdown:
 * ```terminal
 * $ whoami
 * operator
 *
 * # nmap -sS 192.0.2.15
 * Starting Nmap scan...
 * PORT    STATE SERVICE
 * 22/tcp  open  ssh
 * ```
 */
interface TerminalBlockProps {
  /** Raw terminal text content */
  content: string;
  /** Whether to animate typing */
  animate?: boolean;
}

export const TerminalBlock: React.FC<TerminalBlockProps> = ({ content, animate = false }) => {
  // Parse the content to extract prompts, commands, and outputs
  const lines = content.trim().split('\n');
  const terminals: { prompt: string; command: string; output: string; isRoot: boolean }[] = [];

  let currentPrompt = '';
  let currentCommand = '';
  let currentOutput: string[] = [];
  let isRoot = false;

  lines.forEach((line) => {
    // Detect prompt lines ($ or # at start)
    const userPromptMatch = line.match(/^(\$\s*|[a-zA-Z0-9@~:-]+\$\s*)(.*)/);
    const rootPromptMatch = line.match(/^(#\s*|[a-zA-Z0-9@~:-]+#\s*)(.*)/);

    if (rootPromptMatch || userPromptMatch) {
      // Save previous terminal block if exists
      if (currentCommand) {
        terminals.push({
          prompt: currentPrompt,
          command: currentCommand,
          output: currentOutput.join('\n'),
          isRoot,
        });
      }

      // Start new terminal block
      const match = rootPromptMatch || userPromptMatch;
      currentPrompt = match![1].trim();
      currentCommand = match![2];
      currentOutput = [];
      isRoot = !!rootPromptMatch;
    } else {
      // This is output from previous command
      currentOutput.push(line);
    }
  });

  // Save last terminal block
  if (currentCommand) {
    terminals.push({
      prompt: currentPrompt,
      command: currentCommand,
      output: currentOutput.join('\n'),
      isRoot,
    });
  }

  // If no prompts detected, treat entire content as single command+output
  if (terminals.length === 0 && content.trim()) {
    const [firstLine, ...rest] = lines;
    terminals.push({
      prompt: '',
      command: firstLine,
      output: rest.join('\n'),
      isRoot: false,
    });
  }

  return (
    <div className="terminal-block-container space-y-4">
      {terminals.map((terminal, index) => (
        <Terminal
          key={index}
          prompt={terminal.prompt || undefined}
          command={terminal.command}
          output={terminal.output || undefined}
          animate={animate && index === 0} // Only animate first terminal in block
          isRoot={terminal.isRoot}
        />
      ))}
    </div>
  );
};
